import { Divider, Grid, styled } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colors } from "@zapalacorp/tokens";

export const Text = styled("span")(() => ({
  color: "#1D2939",
  fontFamily: "Manrope",
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
}));

export const SectionTitle = styled("span")(() => ({
  fontSize: "16px",
  fontWeight: 700,
  color: "#465869",
  lineHeight: "24px",
  fontFamily: "Manrope",
}));

export const TrackingDivider = styled(Divider)(() => ({
  height: "inherit",
}));

export const TabletBreakpointWrapper = styled("div")(() => ({
  width: "100%",

  "@media (min-width: 600px)": {
    display: "flex",
    flexDirection: "row",
    gap: "0 24px",
  },
}));

export const TabletBreakpointLeftItemsContainer = styled("div")(() => ({
  width: "100%",

  "@media (min-width: 600px)": {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
}));

interface TrackingSectionsProps {
  isFromMaintenance?: boolean;
}

export const SkeletonContainer = styled(Grid)(({ isFromMaintenance }: TrackingSectionsProps) => ({
  ...(isFromMaintenance ? { paddingTop: "12px", paddingBottom: "12px" } : {}),
}));

export const HeaderSection = styled("div")(({ isFromMaintenance }: TrackingSectionsProps) => ({
  gridArea: "header",
  ...(isFromMaintenance ? { paddingTop: "16px", paddingBottom: "16px" } : {}),
}));
export const DetailSection = styled("div")(({ isFromMaintenance }: TrackingSectionsProps) => ({
  gridArea: "details",
  ...(isFromMaintenance ? { paddingBottom: "16px" } : {}),
}));
export const RouterSection = styled("div")(({ isFromMaintenance }: TrackingSectionsProps) => ({
  gridArea: "router",
  ...(isFromMaintenance ? { paddingTop: "16px", paddingBottom: "16px" } : {}),
  "@media screen and (min-width: 600px)": {
    padding: "0 24px",
    ...(isFromMaintenance ? { paddingTop: "16px", paddingBottom: "16px" } : {}),
    borderLeft: `1px solid ${colors.gray200}`,
  },
}));

type WrapperProps = {
  isCustomerPortal: boolean;
};
export const Wrapper = styled("div")(({ isCustomerPortal }: WrapperProps) => ({
  display: "grid",
  gridTemplateAreas: `"header router" "details router"`,
  gridTemplateRows: isCustomerPortal ? `minmax(auto)` : "minmax(auto, 200px)",
  gridTemplateColumns: isCustomerPortal ? "1fr" : "1fr 0.2fr",
  gridColumnGap: 24,
  gridRowGap: 16,
  width: "100%",

  "@media screen and (max-width: 1370px)": {
    gridTemplateAreas: `"header header" "details router"`,
    gridTemplateRows: "1fr",
    gridRowGap: 16,
  },

  "@media screen and (max-width: 768px)": {
    gridTemplateColumns: "minmax(200px, auto)",
    gridTemplateRows: "1fr",
  },

  "@media screen and (max-width: 600px)": {
    gridTemplateAreas: `"header" "details" "router"`,
  },
}));

const styles = makeStyles(() => ({
  topSpacer: {
    marginTop: "30px",
  },
  bottomSpacer: {
    marginBottom: "24px",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "100%",
  },
  wrapper: {
    display: "flex",
    gap: "12px",

    "@media (min-width: 600px)": {
      gap: "24px",
    },
  },
  trackingInfoContainer: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "8px",

    width: "100%",
  },
  header: {
    width: "100%",
  },
  titleMap: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#465869",
    marginBottom: 2,
    fontFamily: "Manrope",
  },
  title: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#465869",
    marginBottom: "24px",
    fontFamily: "Manrope",
  },
  subtitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#9CA6AF",
    marginBottom: "8px",
    whiteSpace: "nowrap",
    fontFamily: "Manrope",
  },
  text: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#465869",
    marginBottom: "24px",
    fontFamily: "Manrope",
  },
  mobileDivider: {
    display: "none",
  },
  mobileInfoGrid: {
    display: "flex",

    "@media (min-width: 600px)": {
      display: "none",
    },
  },
  tabletInfoGrid: {
    display: "none",

    "@media (min-width: 600px) and (max-width: 1370px)": {
      display: "flex",
      height: "100%",
    },
  },
  desktopInfoGrid: {
    display: "none",

    "@media (min-width: 1370px)": {
      display: "flex",
      maxWidth: "25%",
    },
  },
  iconButton: {
    background: "#308CDB",
    color: "#fff",
    pointerEvents: "none",
  },
  loading: {
    position: "absolute",
    top: "0",
    left: "0",
    zIndex: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    width: "100%",
    background: "rgba(0, 0, 0, 0.1)",
  },
  cancelButton: {
    padding: "0px",
    marginTop: "20px",
  },
  lineSeparator: {
    width: "inherit",
    marginTop: "20px",
    marginBottom: "0px",
    border: "1px solid #E0E2E7",
  },
  summarySpan: {
    font: "700 14px/24px Manrope",
    color: "#9ca6af",
    marginBottom: 4,
  },
  summaryText: {
    font: "500 14px/24px Manrope",
    color: "#465869",
  },
  mb16: {
    marginBottom: 16,
  },
  finalPortNameClass: {
    "@media (min-width: 1024px)": {
      marginRight: "20px !important",
    },
  },
  defaultRouterStepperClasses: {
    overflow: "hidden",
    "@media (min-width: 600px) and (max-width: 1370px)": {
      maxWidth: "40%",
    },
  },

  borderRight: {
    "@media screen and (min-width: 961px)": {
      borderRight: `1px solid ${colors.gray200}`,
    },
  },
  paddingDefault: {
    "@media screen and (max-width: 599px)": {
      padding: "16px",
    },
    padding: "16px 24px",
  },
  pComponentLeft: {
    padding: "16px 24px 8px",
    "@media screen and (max-width: 599px)": {
      padding: "12px 16px",
    },
  },
  pComponentRight: {
    padding: "8px 24px 48px",
    "@media screen and (max-width: 599px)": {
      padding: "16px",
    },
  },
}));

export default styles;
